import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input } from '@angular/core'

/**
 * Generic actual-vs-target horizontal bar with a marker line at the
 * target. The data-driven scale picks max(actual, target) as 100% so
 * neither side overflows the track. Color band is decided by the
 * caller via `tone` so this primitive doesn't depend on any specific
 * domain semantics (over/under/ok/unknown).
 *
 * Pulled out of `<app-station-variance-table-batch>`'s touch bar so it
 * can be reused in any column comparing a number to a standard
 * (actual vs. budget, run-time vs. cycle-time, etc.).
 */
export type ProgressBarTone = 'over' | 'under' | 'ok' | 'unknown' | 'neutral'

export interface ProgressBarLayout {
    fillPct: number
    markerPct: number
}

@Component({
    selector: 'app-detail-progress-bar',
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './detail-progress-bar.component.html',
    styleUrls: ['./detail-progress-bar.component.scss']
})
export class DetailProgressBarComponent {
    /** Actual value (e.g. touch minutes). */
    @Input() actual: number | null = null
    /** Target value (e.g. standard minutes). */
    @Input() target: number | null = null
    /** Visual band for the fill. Caller decides — primitive is domain-neutral. */
    @Input() tone: ProgressBarTone = 'neutral'
    /** Optional tooltip on the track. */
    @Input() title: string | null = null
    /** testid prefix for the track + fill + marker (defaults match the legacy
     *  variance-table contract used by the PR #3089 acceptance specs). */
    @Input() testIdPrefix = 'progress-bar'
    /** Hide the marker (e.g. when target is undefined). Defaults to auto: hide
     *  when target is null/0. */
    @Input() showMarker: 'auto' | 'always' | 'never' = 'auto'

    get layout(): ProgressBarLayout {
        const target = this.target ?? 0
        if (!target) return { fillPct: 0, markerPct: 0 }
        const actual = Math.max(this.actual ?? 0, 0)
        const scale = Math.max(actual, target)
        return {
            fillPct: scale > 0 ? (actual / scale) * 100 : 0,
            markerPct: scale > 0 ? (target / scale) * 100 : 0
        }
    }

    get markerVisible(): boolean {
        if (this.showMarker === 'always') return true
        if (this.showMarker === 'never') return false
        return !!(this.target && this.target > 0)
    }

    get trackTestId(): string { return this.testIdPrefix + '-track' }
    get fillTestId(): string { return this.testIdPrefix + '-fill' }
    get markerTestId(): string { return this.testIdPrefix + '-marker' }
}
